import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faSpinner, faCheck, faXmark, faLinkSlash } from "@fortawesome/free-solid-svg-icons";
import { useAccountType } from "../../hooks/useAccountType";
import { ppapiPost, apiErrorMessage } from "../../utils/partnerApi";

/**
 * Partner-side "Agency access" (KOL_CAMPAIGNS_AGENCY_PLAN.md §7.3 D / §7.6):
 * pending requests from agencies (Accept / Decline) + the agencies that
 * currently read this account (Revoke). Rendered on the Security page; the
 * pending banner in Main.js links here.
 */
export default function AgencyAccessSection({ compact = false }) {
  const accountType = useAccountType();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    ppapiPost("partnerListAgencyRequests")
      .then((d) => {
        if (!Array.isArray(d?.links)) { setUnavailable(true); return; }
        setLinks(d.links); setUnavailable(false);
      })
      .catch((e) => { if (e?.response?.status !== 450) setUnavailable(true); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = (l, accept) => {
    setBusy(l.linkId);
    ppapiPost("partnerDecideAgencyLink", { linkId: l.linkId, accept: accept ? 1 : 0 })
      .then(() => { toast.success(accept ? `${l.agencyName} can now view your account.` : "Request declined"); load(); })
      .catch((e) => toast.error(apiErrorMessage(e, "Could not save your decision.")))
      .finally(() => setBusy(null));
  };

  const revoke = (l) => {
    if (!window.confirm(`Stop ${l.agencyName} from viewing your account?`)) return;
    setBusy(l.linkId);
    ppapiPost("revokeAgencyLink", { linkId: l.linkId })
      .then(() => { toast.success("Access removed"); load(); })
      .catch((e) => toast.error(apiErrorMessage(e, "Could not remove access.")))
      .finally(() => setBusy(null));
  };

  if (accountType === "agency" || unavailable) return null;
  const pending = links.filter((l) => String(l.status).toLowerCase() === "pending");
  const accepted = links.filter((l) => String(l.status).toLowerCase() === "accepted");
  if (!loading && compact && pending.length === 0 && accepted.length === 0) return null;

  return (
    <div className="panel" style={{ maxWidth: 900, marginBottom: 24, padding: 18 }}>
      <h3 className="secH" style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 10 }}>
        <FontAwesomeIcon icon={faBuilding} /> Agency access
      </h3>
      <p className="secLead" style={{ marginTop: 0 }}>
        An agency you work with can ask to monitor this account <b>read-only</b>: your dashboard, orders, campaigns and referral
        stats. It never sees balances, withdrawals, API keys or security settings, and cannot change anything. You can revoke at any time.
      </p>

      {loading ? (
        <div style={{ color: "#a7b2ab" }}><FontAwesomeIcon icon={faSpinner} spin /> Loading…</div>
      ) : (
        <>
          {pending.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div className="fldLbl">Pending requests</div>
              {pending.map((l) => (
                <div key={l.linkId} className="notice" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
                  <span className="ni">?</span>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <b>{l.agencyName}</b> wants to view your account
                    <small style={{ display: "block", color: "#7c847e" }}>requested {l.requestedAt ? new Date(l.requestedAt).toLocaleString() : ""}{l.source === "invite" ? " · via invite link" : ""}</small>
                  </div>
                  <button type="button" className="btnPrimary sm" disabled={busy === l.linkId} onClick={() => decide(l, true)}>
                    {busy === l.linkId ? <FontAwesomeIcon icon={faSpinner} spin /> : <><FontAwesomeIcon icon={faCheck} /> Accept</>}
                  </button>
                  <button type="button" className="btnGhost sm" disabled={busy === l.linkId} onClick={() => decide(l, false)}>
                    <FontAwesomeIcon icon={faXmark} /> Decline
                  </button>
                </div>
              ))}
            </div>
          )}

          {accepted.length > 0 ? (
            <div>
              <div className="fldLbl">Agencies with access</div>
              {accepted.map((l) => (
                <div key={l.linkId} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,.06)" }}>
                  <div style={{ flex: 1 }}>
                    <b style={{ color: "#dfe6e0" }}>{l.agencyName}</b>
                    <small style={{ display: "block", color: "#7c847e" }}>since {l.decidedAt ? new Date(l.decidedAt).toLocaleDateString() : "—"}{l.source === "invite" ? " · joined through their invite" : ""}</small>
                  </div>
                  <button type="button" className="btnGhost sm" disabled={busy === l.linkId} onClick={() => revoke(l)}>
                    <FontAwesomeIcon icon={faLinkSlash} /> Revoke
                  </button>
                </div>
              ))}
            </div>
          ) : pending.length === 0 && (
            <div style={{ color: "#7c847e", fontSize: 13 }}>No agency has access to this account.</div>
          )}
        </>
      )}
    </div>
  );
}
